import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

// Flag SVG components for reliable rendering
const FlagGB = () => (
  <svg className="w-6 h-6 rounded" viewBox="0 0 60 30" xmlns="http://www.w3.org/2000/svg">
    <clipPath id="s"><path d="M0,0 v30 h60 v-30 z"/></clipPath>
    <clipPath id="t"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z"/></clipPath>
    <g clipPath="url(#s)">
      <path d="M0,0 v30 h60 v-30 z" fill="#012169"/>
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6"/>
      <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t)" stroke="#C8102E" strokeWidth="4"/>
      <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10"/>
      <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6"/>
    </g>
  </svg>
);

const FlagES = () => (
  <svg className="w-6 h-6 rounded" viewBox="0 0 750 500" xmlns="http://www.w3.org/2000/svg">
    <rect width="750" height="500" fill="#c60b1e"/>
    <rect width="750" height="250" fill="#ffc400" y="125"/>
  </svg>
);

const FlagFR = () => (
  <svg className="w-6 h-6 rounded" viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg">
    <rect width="900" height="600" fill="#ED2939"/>
    <rect width="600" height="600" fill="#fff"/>
    <rect width="300" height="600" fill="#002395"/>
  </svg>
);

const FlagDE = () => (
  <svg className="w-6 h-6 rounded" viewBox="0 0 5 3" xmlns="http://www.w3.org/2000/svg">
    <rect width="5" height="3" fill="#FFCE00"/>
    <rect width="5" height="2" fill="#DD0000"/>
    <rect width="5" height="1" fill="#000"/>
  </svg>
);

const FlagIT = () => (
  <svg className="w-6 h-6 rounded" viewBox="0 0 3 2" xmlns="http://www.w3.org/2000/svg">
    <rect width="3" height="2" fill="#009246"/>
    <rect width="2" height="2" x="1" fill="#fff"/>
    <rect width="1" height="2" x="2" fill="#ce2b37"/>
  </svg>
);

const FlagPT = () => (
  <svg className="w-6 h-6 rounded" viewBox="0 0 600 400" xmlns="http://www.w3.org/2000/svg">
    <rect width="600" height="400" fill="#ff0000"/>
    <rect width="240" height="400" fill="#006600"/>
  </svg>
);

const FlagPL = () => (
  <svg className="w-6 h-6 rounded" viewBox="0 0 16 10" xmlns="http://www.w3.org/2000/svg">
    <rect width="16" height="10" fill="#dc143c"/>
    <rect width="16" height="5" fill="#fff"/>
  </svg>
);

const FlagIN = () => (
  <svg className="w-6 h-6 rounded" viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg">
    <rect width="900" height="600" fill="#fff"/>
    <rect width="900" height="200" fill="#ff9933"/>
    <rect width="900" height="200" y="400" fill="#138808"/>
    <circle cx="450" cy="300" r="60" fill="#000080"/>
  </svg>
);

const FlagPK = () => (
  <svg className="w-6 h-6 rounded" viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg">
    <rect width="900" height="600" fill="#01411C"/>
    <rect width="225" height="600" fill="#fff"/>
  </svg>
);

const flagComponents = {
  en: FlagGB,
  es: FlagES,
  fr: FlagFR,
  de: FlagDE,
  it: FlagIT,
  pt: FlagPT,
  pl: FlagPL,
  hi: FlagIN,
  pa: FlagIN,
  gu: FlagIN,
  bn: FlagIN,
  ta: FlagIN,
  ur: FlagPK,
};

const languages = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
  { code: 'pa', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ' },
  { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்' },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو' },
];

const LanguageSelector = ({ variant = 'default', className = '', showLabel = true }) => {
  const { i18n } = useTranslation();
  
  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];
  const FlagComponent = flagComponents[currentLang.code] || FlagGB;

  const handleLanguageChange = async (langCode) => {
    try {
      await i18n.changeLanguage(langCode);
      localStorage.setItem('i18nextLng', langCode);
      window.dispatchEvent(new Event('languageChanged'));
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  const renderFlag = (code) => {
    const Flag = flagComponents[code] || FlagGB;
    return <Flag />;
  };

  // Flag-only variant - most visible
  if (variant === 'flag-only') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm"
            className={`h-10 w-10 p-0 rounded-full hover:bg-gray-100/20 ${className}`} 
            data-testid="language-selector"
            title={`Language: ${currentLang.name}`}
          >
            <FlagComponent />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 max-h-80 overflow-y-auto">
          <div className="p-2 border-b mb-2">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-2">
              <Globe size={14} />
              Select Language
            </p>
          </div>
          {languages.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`cursor-pointer flex items-center gap-3 py-2.5 ${
                i18n.language === lang.code ? 'bg-primary/10 text-primary font-medium' : ''
              }`}
              data-testid={`lang-${lang.code}`}
            >
              {renderFlag(lang.code)}
              <div className="flex-1">
                <p className="font-medium">{lang.name}</p>
                <p className="text-xs text-gray-500">{lang.nativeName}</p>
              </div>
              {i18n.language === lang.code && (
                <Check size={18} className="text-primary" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Compact variant
  if (variant === 'compact') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className={`relative ${className}`} 
            data-testid="language-selector"
            title={`Language: ${currentLang.name}`}
          >
            <FlagComponent />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48 max-h-80 overflow-y-auto">
          {languages.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`cursor-pointer flex items-center gap-3 ${
                i18n.language === lang.code ? 'bg-primary/10 text-primary font-medium' : ''
              }`}
              data-testid={`lang-${lang.code}`}
            >
              {renderFlag(lang.code)}
              <div className="flex-1">
                <p className="text-sm">{lang.name}</p>
              </div>
              {i18n.language === lang.code && (
                <Check size={16} className="text-primary" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Default variant - flag with label
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className={`gap-2 ${showLabel ? 'min-w-[140px]' : ''} justify-start ${className}`} 
          data-testid="language-selector"
        >
          <FlagComponent />
          {showLabel && <span className="flex-1 text-left">{currentLang.name}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 max-h-80 overflow-y-auto">
        <div className="p-2 border-b mb-2">
          <p className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
            <Globe size={14} />
            Select Language
          </p>
        </div>
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={`cursor-pointer flex items-center gap-3 py-2.5 ${
              i18n.language === lang.code ? 'bg-primary/10 text-primary' : ''
            }`}
            data-testid={`lang-${lang.code}`}
          >
            {renderFlag(lang.code)}
            <div className="flex-1">
              <p className="font-medium">{lang.name}</p>
              <p className="text-xs text-gray-500">{lang.nativeName}</p>
            </div>
            {i18n.language === lang.code && (
              <Check size={18} className="text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelector;
